import { Flex, Text } from '@chakra-ui/react';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Modal,
  Checkbox,
  Button,
  Banner,
  Pagination,
  SearchInput,
  Table,
  useToast
} from '@morressier/alembic';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchJournalBoardMembers, validatorForAddReviewer } from 'src/api/queries';
import { IReviewer } from '@publishing-engine/domain/models';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import {
  categorizeDuplicateReviewers,
  findBlockedReviewers,
  getBlockedReviewerErrorMessage
} from 'src/utils/ReviewersValidations';
import ListLoader from 'src/components/ListLoader';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import ReviewersInfoWarningModal from './ReviewersInfoWarningModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  submissionContextId: string;
  submissionId: string;
  existingReviewers: IReviewer[] | Pick<IReviewer, 'email'>[];
  blockedReviewers: IBlockedReviewer[];
  onAddBoardMembers: (userIds: string[]) => void;
}

const LIMIT = 10;

const AddBoardMembersModal = ({
  isOpen,
  onClose,
  organizationId,
  submissionContextId,
  submissionId,
  existingReviewers,
  blockedReviewers,
  onAddBoardMembers
}: Props) => {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<
    Record<string, boolean>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isValidating, setIsValidating] = useState(false);
  const [infoMessages, setInfoMessages] = useState<Record<string, string>>({});
  const [warningMessages, setWarningMessages] = useState<
    Record<string, string>
  >({});
  const [isInfoWarningOpen, setIsInfoWarningOpen] = useState(false);

  const handleSearch = () => {
    setSearchValue(searchInput.trim());
    setPage(1);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'journal-board-members',
      organizationId,
      submissionContextId,
      searchValue,
      page
    ],
    queryFn: () =>
      fetchJournalBoardMembers({
        organizationId,
        submissionContextId,
        name: searchValue,
        offset: (page - 1) * LIMIT,
        limit: LIMIT
      }),
    enabled: !!organizationId && !!submissionContextId,
    keepPreviousData: true
  });

  const items = data?.response?.items ?? [];
  const total = data?.response?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);
  const startIndex = (page - 1) * LIMIT + 1;
  const endIndex = Math.min(page * LIMIT, total);

  const selectedCount = Object.values(selectedUserIds).filter(Boolean).length;

  const toggleMember = (userId: string) => {
    setSelectedUserIds(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
    setValidationErrors({});
  };

  const handleAddBoardMembers = async () => {
    const selectedMembers = items.filter(
      member => selectedUserIds[member.id]
    );

    if (!selectedMembers.length) return;

    setIsValidating(true);
    setValidationErrors({});

    const reviewers = selectedMembers.map(member => ({ email: member.email }));
    const errors: Record<string, string> = {};

    // 1) Duplicate / already-added checks
    const duplicateCategories = categorizeDuplicateReviewers(
      reviewers,
      existingReviewers
    );
    Object.entries(duplicateCategories).forEach(
      ([email, { isInputDuplicate, isAlreadyInvited }]) => {
        if (isInputDuplicate && isAlreadyInvited) {
          errors[email] =
            'Duplicate email IDs added, clear the duplicates to continue. The invitation for the email ID(s) entered is already sent, reinvite them or manage them in the reviews tab.';
        } else if (isInputDuplicate) {
          errors[email] =
            'Duplicate email IDs added, clear the duplicates to continue.';
        } else if (isAlreadyInvited) {
          errors[email] =
            'The invitation for the email ID entered is already sent, reinvite them or manage them in the reviews tab.';
        }
      }
    );

    // 2) Blocked reviewers
    const blocked = findBlockedReviewers(reviewers, blockedReviewers);
    blocked.forEach(({ index, blockReason }) => {
      const email = reviewers[index]?.email;
      if (email) {
        errors[email] = getBlockedReviewerErrorMessage(blockReason, email);
      }
    });

    // 3) Async check API
    const validation = await validatorForAddReviewer(
      submissionContextId,
      submissionId,
      reviewers.map(r => r.email)
    );

    if (!validation?.length) {
      toast({
        variant: 'error',
        title: 'Error validating board members',
        description: 'Please try again later.'
      });
      setIsValidating(false);
      return;
    }

    const disallowedInfoMap: Record<string, string> = {};
    const allowedInfoMap: Record<string, string> = {};
    const warningMap: Record<string, string> = {};

    validation.forEach(item => {
      if (item.error?.length) {
        errors[item.email] = item.error.map(err => err.message).join(', ');
      } else if (!item.isUserExist) {
        errors[item.email] =
          'User does not exist. Please create the user and assign the review';
      } else if (item.userStatus === 'invited') {
        errors[item.email] =
          'Account creation pending from user. You cannot assign at the moment.';
      } else if (item.userStatus === 'deactivated') {
        errors[item.email] = 'This account has been deactivated.';
      }

      if (item.info?.length) {
        const disallowed = item.info.filter(i => !i.isAllowed);
        const allowed = item.info.filter(i => i.isAllowed);

        if (disallowed.length) {
          disallowedInfoMap[item.email] = disallowed
            .map(i => i.message)
            .join(', ');
        }

        if (allowed.length) {
          allowedInfoMap[item.email] = allowed.map(i => i.message).join(', ');
        }
      }

      if (item.warning?.length) {
        warningMap[item.email] = item.warning.map(w => w.message).join(', ');
      }
    });

    Object.entries(disallowedInfoMap).forEach(([email, message]) => {
      errors[email] = message;
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsValidating(false);
      return;
    }

    if (
      Object.keys(allowedInfoMap).length > 0 ||
      Object.keys(warningMap).length > 0
    ) {
      setInfoMessages(allowedInfoMap);
      setWarningMessages(warningMap);
      setIsInfoWarningOpen(true);
      setIsValidating(false);
      return;
    }

    setIsValidating(false);
    onAddBoardMembers(selectedMembers.map(member => member.id));
    onClose();
  };

  const isTableLoading = isLoading || isFetching;

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="960px">
      <Flex direction="column" gap={6} p={8}>
        <Flex gap={3} direction="column" alignItems="flex-start">
          <Text textStyle="headingGroup">Assign Board Members</Text>
          <Text textStyle="textBody">
            {isTableLoading ? (
              'Loading board members...'
            ) : (
              <>
                Showing{' '}
                <Text as="span" fontWeight="bold">
                  {total === 0
                    ? total
                    : `${startIndex} - ${endIndex} of ${total}`}
                </Text>{' '}
                board members
              </>
            )}
          </Text>

          <SearchInput
            placeholder="Search board members by name and email"
            name="searchParams"
            value={searchInput}
            disabled={isTableLoading}
            onChange={e => setSearchInput(e.currentTarget.value)}
            onInputClear={() => {
              setSearchInput('');
              setSearchValue('');
              setPage(1);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
        </Flex>

        <Table>
          {!isTableLoading && items.length > 0 && (
            <Table.HeaderRow gap={2}>
              <Table.HeaderCell width="40px" />
              <Table.HeaderCell flex="1">Name</Table.HeaderCell>
              <Table.HeaderCell flex="1">Email</Table.HeaderCell>
            </Table.HeaderRow>
          )}
          <Table.Body>
            {isTableLoading ? (
              <ListLoader />
            ) : items.length === 0 ? (
              <SubmissionLoader
                title="No Board Members Found"
                subTitle="We could not find any board members matching the provided name or email."
                bgColor="neutralSecondaryFill"
              />
            ) : (
              items.map(member => (
                <Table.Row key={member.id} gap={2}>
                  <Table.RowCell width="40px">
                    <Checkbox
                      name={`board-member-${member.id}`}
                      id={`board-member-${member.id}`}
                      ariaLabel="Select board member"
                      isChecked={!!selectedUserIds[member.id]}
                      onChange={() => toggleMember(member.id)}
                    >
                      {''}
                    </Checkbox>
                  </Table.RowCell>
                  <Table.RowCell flex="1">
                    {member.firstName} {member.lastName}
                  </Table.RowCell>
                  <Table.RowCell flex="1">{member.email}</Table.RowCell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {totalPages > 1 && !isTableLoading && (
          <Flex justifyContent="center">
            <Pagination
              ariaLabel="Pagination"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Flex>
        )}

        {Object.keys(validationErrors).length > 0 && (
          <Flex direction="column" gap={2}>
            {Object.entries(validationErrors).map(([email, message]) => (
              <Banner
                key={email}
                variant="error"
                heading={email}
                content={message}
              />
            ))}
          </Flex>
        )}

        <Flex justifyContent="space-between" gap={2}>
          <Button variant="caution" onClick={onClose}>
            Exit
          </Button>
          <Button
            variant="primary"
            iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
            onClick={handleAddBoardMembers}
            isLoading={isValidating}
            isDisabled={!selectedCount || isValidating}
          >
            Assign Board Members {selectedCount > 0 && `(${selectedCount})`}
          </Button>
        </Flex>
      </Flex>

      <ReviewersInfoWarningModal
        isOpen={isInfoWarningOpen}
        infoMessages={infoMessages}
        warningMessages={warningMessages}
        onCancel={() => {
          setIsInfoWarningOpen(false);
          setInfoMessages({});
          setWarningMessages({});
        }}
        onContinue={() => {
          setIsInfoWarningOpen(false);
          setInfoMessages({});
          setWarningMessages({});
          const selected = items.filter(m => selectedUserIds[m.id]);
          onAddBoardMembers(selected.map(member => member.id));
          onClose();
        }}
      />
    </Modal>
  );
};

export default AddBoardMembersModal;
